import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./ChooseCarModal.css";
import BrandPopup from "./BrandPopup"; // new popup component
import ModelPopup from "./ModelPopup";
import FuelPopup from "./FuelPopup";
import axios from "axios";
import Swal from "sweetalert2";
import CryptoJS from "crypto-js";
import { useAlert } from "../context/AlertContext";
import { v4 as uuidv4 } from "uuid";
import { saveUserFromVerifyOtp } from "../helper/authHelper";

const validateVehicleNumber = (number) => {
    const cleaned = (number || "").toUpperCase().trim();
    const regex = /^(?=.*[A-Z])(?=.*\d)[A-Z0-9]{6,15}$/;
    return regex.test(cleaned);
};

const buildSelectedCarDetails = ({ brandItem, modelItem, fuelItem, vehicle = {}, formData = {}, vehicleID = 0 }) => {
    const registrationNumber = (
        vehicle.registrationNumber ||
        vehicle.vehicleNumber ||
        vehicle.VehicleNumber ||
        formData.registrationNumber ||
        ""
    ).toString().toUpperCase();

    const resolvedVehicleId = Number(
        vehicleID ||
        vehicle.VehicleID ||
        vehicle.vehicleID ||
        vehicle.id ||
        0
    ) || 0;

    return {
        brand: brandItem || null,
        model: modelItem || null,
        fuel: fuelItem || null,
        id: resolvedVehicleId,
        VehicleID: resolvedVehicleId,
        vehicleNumber: registrationNumber,
        VehicleNumber: registrationNumber,
        registrationNumber,
        brandID: Number(vehicle.brandID || vehicle.BrandID || brandItem?.id) || 0,
        modelID: Number(vehicle.modelID || vehicle.ModelID || modelItem?.id) || 0,
        fuelTypeID: Number(vehicle.fuelTypeID || vehicle.FuelTypeID || fuelItem?.id) || 0,
        kilometersDriven: Number(
            vehicle.kilometersDriven ||
            vehicle.KilometersDriven ||
            formData.kilometerDriven ||
            formData.kilometersDriven ||
            0
        ) || 0,
        kilometerDriven: (
            vehicle.kilometerDriven ||
            vehicle.kilometersDriven ||
            vehicle.KilometersDriven ||
            formData.kilometerDriven ||
            formData.kilometersDriven ||
            ""
        ),
        yearOfPurchase: Number(vehicle.yearOfPurchase || vehicle.YearOfPurchase || formData.yearOfPurchase || 0) || 0,
        engineType: vehicle.engineType || vehicle.EngineType || formData.engineType || "",
        transmissionType: vehicle.transmissionType || vehicle.TransmissionType || formData.transmissionType || "",
    };
};

const ChooseCarModal = ({ isVisible, onClose, onCarSaved }) => {
	// Alert API (optional)
	const { showAlert } = useAlert();
	const BASE_URL = process.env.REACT_APP_CARBUDDY_BASE_URL;
	const [selectionMethod, setSelectionMethod] = useState("manual"); // default manual
	const [registrationNumber, setRegistrationNumber] = useState("");
	const [carType, setCarType] = useState("");
	const [brand, setBrand] = useState(null);
	const [brands, setBrands] = useState([]);
	const [models, setModels] = useState([]);
	const [fuels, setFuels] = useState([]);
	const [model, setModel] = useState("");
	const [fuel, setFuel] = useState("");
	const [showBrandPopup, setShowBrandPopup] = useState(false);
	const [showModelPopup, setShowModelPopup] = useState(false);
	const [showFuelPopup, setShowFuelPopup] = useState(false);
	const [loadingBrands, setLoadingBrands] = useState(true);
	const [loadingModels, setLoadingModels] = useState(false);
	const modalRef = useRef();
	const imageBaseURL = process.env.REACT_APP_CARBUDDY_IMAGE_URL;
	const BaseURL = process.env.REACT_APP_CARBUDDY_BASE_URL;
    const secretKey = process.env.REACT_APP_ENCRYPT_SECRET_KEY;

    // Auth state (inline OTP login when not logged in)
    const [identifier, setIdentifier] = useState("");
    const [otp, setOtp] = useState("");
    const [otpSent, setOtpSent] = useState(false);
    const [timer, setTimer] = useState(0);
    const [otpExpired, setOtpExpired] = useState(false);
    const [loginLoading, setLoginLoading] = useState(false);
    const [isLoggedIn, setIsLoggedIn] = useState(!!JSON.parse(localStorage.getItem("user"))?.token);
    const regCheckTimeoutRef = useRef(null);
    const [savedVehicles, setSavedVehicles] = useState([]);
    const [loadingSavedVehicles, setLoadingSavedVehicles] = useState(false);
    const [selectedSavedVehicleId, setSelectedSavedVehicleId] = useState("");
    const [isLoadingVehicleData, setIsLoadingVehicleData] = useState(false);
    const [vehicleNumberTimeout, setVehicleNumberTimeout] = useState(null);

	// Vehicle detail form data
	const [formData, setFormData] = useState({
		registrationNumber: "",
		yearOfPurchase: "",
		engineType: "",
		kilometerDriven: "",
		transmissionType: "",
		brandID: "",
		modelID: "",
		fuelTypeID: "",
	});

		// Validation states
    const [yearError, setYearError] = useState(false);
	const [regError, setRegError] = useState(false);
	const [regMessage, setRegMessage] = useState("");
	/** Shown when user tries Save without Brand + Model + Fuel (all required for new car) */
	const [selectionError, setSelectionError] = useState("");

	// Bind IDs on selection
	useEffect(() => {
		setFormData((prev) => ({
			...prev,
			brandID: brand || "",
			modelID: model || "",
			fuelTypeID: fuel || "",
		}));
	}, [brand, model, fuel]);

	useEffect(() => {
		if (brand && model && fuel) setSelectionError("");
	}, [brand, model, fuel]);

	// Reset modal form when it is opened from header
	useEffect(() => {
		if (!isVisible) return;
		try {
			setSelectionMethod("manual");
			setBrand(null);
			setModel("");
			setFuel("");
			setRegistrationNumber("");
			setFormData({
				registrationNumber: "",
				yearOfPurchase: "",
				engineType: "",
				kilometerDriven: "",
				transmissionType: "",
				brandID: "",
				modelID: "",
				fuelTypeID: "",
			});
			setSelectionError("");
		} catch (_) { /* no-op */ }
	}, [isVisible]);

    // OTP countdown
    useEffect(() => {
        let interval;
        if (otpSent && timer > 0) {
            interval = setInterval(() => setTimer((t) => t - 1), 1000);
        } else if (otpSent && timer === 0) {
            setOtpExpired(true);
        }
        return () => clearInterval(interval);
    }, [otpSent, timer]);

    // Monitor login status changes
    useEffect(() => {
        const checkLoginStatus = () => {
            const user = JSON.parse(localStorage.getItem("user"));
            setIsLoggedIn(!!user?.token);
        };

        // Check on mount
        checkLoginStatus();

        // Listen for user profile updates
        window.addEventListener("userProfileUpdated", checkLoginStatus);
        
        return () => {
            window.removeEventListener("userProfileUpdated", checkLoginStatus);
        };
    }, []);

    const getDeviceId = () => {
        let deviceId = localStorage.getItem("deviceId");
        if (!deviceId) {
            deviceId = uuidv4();
            localStorage.setItem("deviceId", deviceId);
        }
        return deviceId;
    };

    const handleSendOTP = async (e) => {
        e?.preventDefault?.();
        const sanitized = (identifier || "").replace(/\D/g, "");
        if (!/^\d{10}$/.test(sanitized)) {
            showAlert && showAlert("Enter a valid 10-digit mobile number.", "warning");
            setOtpSent(false);
            return;
        }
        setLoginLoading(true);
        try {
            await axios.post(`${BaseURL}Auth/send-otp`, { loginId: sanitized });
            setOtpSent(true);
            setOtpExpired(false);
            setTimer(60);
        } catch (error) {
            console.error("Error sending OTP:", error);
            showAlert && showAlert("Failed to send OTP. Please try again.");
        } finally {
            setLoginLoading(false);
        }
    };

    const handleVerifyOTP = async (e) => {
        e?.preventDefault?.();
        const sanitized = (identifier || "").replace(/\D/g, "");
        if (!/^\d{10}$/.test(sanitized)) {
            showAlert && showAlert("Enter a valid 10-digit mobile number.", "warning");
            return;
        }
        const deviceId = getDeviceId();
        setLoginLoading(true);
        try {
            const response = await axios.post(`${BaseURL}Auth/verify-otp`, {
                loginId: sanitized,
                otp,
                deviceToken: "web-token",
                deviceId,
            });

            saveUserFromVerifyOtp(response.data, { phone: sanitized, name: response.data?.name || "GUEST" });
            setIsLoggedIn(true);
            setOtpSent(false);
            setIdentifier("");
            setOtp("");
            // showAlert && showAlert("Signed in successfully", "success");
            
            // Dispatch event to update header
            window.dispatchEvent(new CustomEvent("userProfileUpdated"));
        } catch (error) {
            console.error("Error verifying OTP:", error);
            showAlert && showAlert("Invalid OTP. Please try again.", "error");
        } finally {
            setLoginLoading(false);
        }
    };

    const getDecryptedCustAuth = () => {
        const user = JSON.parse(localStorage.getItem("user"));
        if (!user?.token || !user?.id) return { custId: null, token: null };
        try {
            const bytes = CryptoJS.AES.decrypt(user.id, secretKey);
            const decryptedCustId = bytes.toString(CryptoJS.enc.Utf8);
            return { custId: decryptedCustId, token: user.token };
        } catch (_) {
            return { custId: null, token: null };
        }
    };

    const checkVehicleExists = async (vehicleNumberUpper) => {
        const { custId, token } = getDecryptedCustAuth();
        if (!custId || !token || !vehicleNumberUpper) return;
        try {
            const res = await axios.get(
                `${BaseURL}CustomerVehicles/CustId?vehicleNumber=${encodeURIComponent(vehicleNumberUpper)}&custId=${encodeURIComponent(custId)}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            const data = res?.data;
            const exists = Array.isArray(data) ? data.length > 0 : (data?.status === true && (Array.isArray(data?.data) ? data.data.length > 0 : !!data?.vehicleID));
            if (exists) {
                Swal.fire({
                    title: 'Already Exists',
                    text: 'This vehicle is already saved. Please select it from your saved cars.',
                    icon: 'info',
                    confirmButtonText: 'OK'
                });
            }
        } catch (err) {
            // Silent fail; do not block user typing
            console.warn('Vehicle check failed', err?.response?.data || err?.message);
        }
    };

    // Fetch vehicle data by registration number
    const fetchVehicleByNumber = async (vehicleNumber) => {
		const { custId, token } = getDecryptedCustAuth();
        if (!vehicleNumber || vehicleNumber.length < 5) return;
        
        setIsLoadingVehicleData(true);
        try {
            const response = await axios.get(
                `${BaseURL}CustomerVehicles/VehicleNumber?VehicleNumber=${encodeURIComponent(vehicleNumber)}&CustId=${encodeURIComponent(custId)}`
            );

            if (response.data && Array.isArray(response.data) && response.data.length > 0) {
                const vehicleData = response.data[0];
				
                
                // Update form data with fetched vehicle information
                setFormData(prev => ({
                    ...prev,
                    registrationNumber: vehicleData.VehicleNumber,
                    yearOfPurchase: vehicleData.YearOfPurchase,
                    engineType: vehicleData.EngineType || "",
                    kilometerDriven: vehicleData.KilometersDriven || "",
                    transmissionType: vehicleData.TransmissionType || "",
					fuelTypeID: vehicleData.FuelTypeID,
                }));

                // Update brand, model, and fuel selections
                if (vehicleData.BrandID) {
                    setBrand(vehicleData.BrandID);
                    
                    // Fetch models for the brand to get the updated list
                    const loadedModels = await fetchModels(vehicleData.BrandID);
                    
                    // Update model selection
                    if (vehicleData.ModelID) {
                        setModel(vehicleData.ModelID);
                        
                        // Update the models state to include the vehicle image from API
                        if (vehicleData.VehicleImage && Array.isArray(loadedModels)) {
                            const updatedModels = loadedModels.map(model => {
                                if (model.id === vehicleData.ModelID) {
                                    return {
                                        ...model,
                                        logo: `${imageBaseURL}${vehicleData.VehicleImage.startsWith("/") ? vehicleData.VehicleImage.slice(1) : vehicleData.VehicleImage}`
                                    };
                                }
                                return model;
                            });
                            setModels(updatedModels);
                        }
                    }
                }
                
                if (vehicleData.FuelTypeID) {
                    // Ensure fuels are loaded before setting fuel
                    if (fuels.length === 0) {
                        await fetchFuels();
                    }
                    
                    // Convert to number to ensure type matching
                    const fuelId = Number(vehicleData.FuelTypeID);
                    setFuel(fuelId);
                    
                    console.log("Setting fuel ID:", fuelId, "Fuel name:", vehicleData.FuelTypeName);
                    console.log("Available fuels:", fuels);
                    console.log("Matching fuel:", fuels.find(f => f.id === fuelId));
                }

                // Show success message
                Swal.fire({
                    title: 'Vehicle Found!',
                    text: `Found ${vehicleData.BrandName} ${vehicleData.ModelName} - ${vehicleData.FuelTypeName}`,
                    icon: 'success',
                    timer: 2000,
                    showConfirmButton: false,
                    toast: true,
                    position: 'top-end'
                });
            }
			else{

				if(response?.message === 'You have already added this vehicle.'){
					Swal.fire({
						title: 'Already Exists',
						text: 'This vehicle is already saved. Please select it from your saved cars.',
						icon: 'info',
						confirmButtonText: 'OK'
					});
				}
				else{
					Swal.fire({
						title: 'Error',
						text: response.data.message,
						icon: 'error',
						timer: 2000,
					});
				}
			}
        } catch (error) {
            console.error("Error fetching vehicle data:", error);
			// Swal.fire({
			// 	title: 'Error',
			// 	text: error.response.data.message,
			// 	icon: 'error',
			// 	timer: 2000,
			// 	showConfirmButton: false,
			// 	toast: true,
			// 	position: 'top-end'
			// });

            // Don't show error to user as this is optional functionality
        } finally {
            setIsLoadingVehicleData(false);
        }
    };

    // Debounced vehicle number change handler
    const handleVehicleNumberChange = (e) => {
        const { value } = e.target;
        const upperValue = value.toUpperCase();
        
        // Update the input value immediately
        setFormData((p) => ({ ...p, registrationNumber: upperValue }));

         // Validate registration number
         const isValid = validateVehicleNumber(upperValue);
         const error = !isValid && upperValue.length > 0;
         const message = error ? "Invalid registration number format." : "";

         setRegError(error);
         setRegMessage(message);
 
		 // Trigger API verification immediately when exactly 10 digits are entered
		 if (upperValue.length === 10) {
			 fetchVehicleByNumber(upperValue);
			 if (isLoggedIn) {
			 }
		 }
    };

	const fetchSavedVehicles = async () => {
		const { custId, token } = getDecryptedCustAuth();
		if (!custId || !token) {
			setSavedVehicles([]);
			return;
		}
		setLoadingSavedVehicles(true);
		try {
			const res = await axios.get(`${BaseURL}CustomerVehicles/CustId?CustId=${encodeURIComponent(custId)}`, {
				headers: { Authorization: `Bearer ${token}` }
			});
			const list = Array.isArray(res?.data) ? res.data : (Array.isArray(res?.data?.data) ? res.data.data : []);
			setSavedVehicles(list || []);
		} catch (err) {
			console.warn('Failed to fetch saved vehicles', err?.response?.data || err?.message);
			setSavedVehicles([]);
		} finally {
			setLoadingSavedVehicles(false);
		}
	};


    // Fetch saved vehicles for logged-in users
    useEffect(() => {
  
        if (isLoggedIn) fetchSavedVehicles();
    }, [isLoggedIn]);

    // Debug fuel changes
    useEffect(() => {
        // console.log("Fuel state changed:", fuel, "Available fuels:", fuels);
        if (fuel) {
            const selectedFuel = fuels.find(f => f.id === fuel);
            console.log("Selected fuel:", selectedFuel);
        }
    }, [fuel, fuels]);

    const handleChooseSavedVehicle = () => {
        const v = savedVehicles.find(x => (x.VehicleID || x.vehicleID) === Number(selectedSavedVehicleId));
        if (!v) return;
        // Populate form and selections
        const vehicleNumber = (v.VehicleNumber || v.VehicleRegNo || "").toString().toUpperCase();
        // setFormData(p => ({
        //     ...p,
        //     registrationNumber: vehicleNumber,
        //     yearOfPurchase: v.YearOfPurchase || "",
        //     engineType: v.EngineType || "",
        //     kilometerDriven: v.KilometersDriven || "",
        //     transmissionType: v.TransmissionType || "",
        // }));
        if (v.BrandID) setBrand(v.BrandID);
        if (v.ModelID) setModel(v.ModelID);
        if (v.FuelTypeID) setFuel(v.FuelTypeID);

        // Prepare and store selectedCarDetails in localStorage
        const selectedCarDetails = buildSelectedCarDetails({
            brandItem: {
                id: v.BrandID,
                name: v.BrandName,
                logo: v.BrandLogo ? `${imageBaseURL}${v.BrandLogo}` : undefined,
            },
            modelItem: {
                id: v.ModelID,
                name: v.ModelName,
                logo: v.VehicleImage ? `${imageBaseURL}${v.VehicleImage}` : undefined,
            },
            fuelItem: {
                id: v.FuelTypeID,
                name: v.FuelTypeName,
                logo: v.FuelImage ? `${imageBaseURL}${v.FuelImage}` : undefined,
            },
            vehicle: v,
            formData: {
                registrationNumber: vehicleNumber,
                yearOfPurchase: v.YearOfPurchase || "",
                engineType: v.EngineType || "",
                kilometerDriven: v.KilometersDriven || "",
                transmissionType: v.TransmissionType || "",
            },
            vehicleID: v.VehicleID || v.vehicleID,
        });
        try {
            localStorage.setItem('selectedCarDetails', JSON.stringify(selectedCarDetails));
        } catch (_) {}

        // Show confirmation toast
        Swal.fire({
            title: 'Vehicle Selected',
            text: 'We filled your form with the saved car details.',
            icon: 'success',
            timer: 1800,
            showConfirmButton: false,
            toast: true,
            position: 'top-end'
        });
        // Ensure the manual form is visible with populated fields
        try { setSelectionMethod('manual'); } catch (_) {}
        try { window.dispatchEvent(new CustomEvent('selectedCarUpdated')); } catch (_) {}
        if (onCarSaved) {
            onCarSaved(selectedCarDetails);
        }
        if (onClose) onClose();
    };

	useEffect(() => {
		const fetchBrands = async () => {
			setLoadingBrands(true);
			try {
				const token = JSON.parse(localStorage.getItem("user"))?.token;
				const response = await axios.get(`${BASE_URL}VehicleBrands/GetVehicleBrands`, {
					headers: {
						Authorization: `Bearer ${token}`,
					},
				});
				if (response.data?.status && Array.isArray(response.data.data)) {
					const formattedBrands = response.data.data
						.filter((b) => b.IsActive === true)
						.map((b) => ({
							id: b.BrandID,
							name: b.BrandName,
							logo: `${imageBaseURL}${b.BrandLogo.startsWith("/") ? b.BrandLogo.slice(1) : b.BrandLogo}`,
						}));
					// console.log("Formatted brands:", formattedBrands);
					setBrands(formattedBrands);
				}
			} catch (err) {
				console.error("Failed to fetch brands", err);
			} finally {
				setLoadingBrands(false);
			}
		};

		fetchBrands();
		fetchFuels();
	}, []);

	const fetchModels = async (brandId) => {
		setLoadingModels(true);
		try {
			const token = JSON.parse(localStorage.getItem("user"))?.token;
			const response = await axios.get(`${BASE_URL}VehicleModels/BrandId?brandid=${brandId}`, {
				headers: {
					Authorization: `Bearer ${token}`,
				},
			});

			console.log("Raw models data:", response.data);

			if (Array.isArray(response.data)) {
				console.log("Raw models data:", response.data);
				const getImageUrl = (path) => {
					if (!path) return "https://via.placeholder.com/100?text=No+Image";
					const fileName = path.split("/").pop();
					return `${imageBaseURL}${path.startsWith("/") ? path.slice(1) : path}`;
				};
				const filteredModels = response.data
					.filter((m) => m.BrandID === brandId && m.IsActive)
					.map((m) => ({
						id: m.ModelID,
						name: m.ModelName,
						logo: getImageUrl(m.VehicleImage), // Use the full valid image URL
					}));

				setModels(filteredModels);
				return filteredModels; // Return the models array
			} else {
				setModels([]);
				console.error("Error fetching models:", response.data);
				return []; // Return empty array
			}
		} catch (error) {
			console.error("Error fetching models:", error);
			return []; // Return empty array on error
		} finally {
			setLoadingModels(false);
		}
	};

	const fetchFuels = async () => {
		try {
			const token = JSON.parse(localStorage.getItem("user"))?.token;
			const res = await axios.get(`${BASE_URL}FuelTypes/GetFuelTypes`, {
				headers: {
					Authorization: `Bearer ${token}`,
				},
			});
			if (res.data?.status && Array.isArray(res.data.data)) {
				const formatted = res.data.data
					.filter((f) => f.IsActive)
					.map((f) => {
						const fileName = f.FuelImage?.split("/").pop();
						const encodedFileName = encodeURIComponent(fileName);
						const imageUrl = `${imageBaseURL}${f.FuelImage}`;

						return {
							id: f.FuelTypeID,
							name: f.FuelTypeName,
							image: imageUrl,
						};
					});
				setFuels(formatted);
			}
		} catch (err) {
			console.error("Failed to fetch fuels", err);
		}
	};

	const handleSubmit = async (e) => {
		if (e) {
			e.preventDefault();
			e.stopPropagation();
		}

		// Required fields validation for registration number and year of purchase
		if (!formData.registrationNumber) {
			if (showAlert) showAlert("Error", "Please enter registration number.", 3000, "error");
			return;
		}

		if (regError) {
			return;
		}

		if (!brand || !model || !fuel) {
			const parts = [
				!brand ? "Brand" : null,
				!model ? "Model" : null,
				!fuel ? "Fuel type" : null,
			].filter(Boolean);
			const msg = `Choose ${parts.join(", ")} — all three are required to save a new car.`;
			setSelectionError(msg);
			if (showAlert) {
				showAlert("Required", msg, 4500, "error");
			} else {
				alert(msg);
			}
			return;
		}
		setSelectionError("");

		const bid = Number(formData.brandID || brand);
		const mid = Number(formData.modelID || model);
		const fid = Number(formData.fuelTypeID || fuel);
		if (!bid || !mid || !fid) {
			const msg = "Brand, Model, and Fuel must all be selected (valid choices) before saving.";
			setSelectionError(msg);
			if (showAlert) showAlert("Required", msg, 4500, "error");
			return;
		}

		const currentYear = new Date().getFullYear();
		const year = parseInt(formData.yearOfPurchase, 10);
		if (isNaN(year) || year < 1900 || year > currentYear) {
			setYearError(true);
			return;
		}

		const selectedCarDetails = buildSelectedCarDetails({
			brandItem: brands.find((b) => b.id === brand),
			modelItem: models.find((m) => m.id === model),
			fuelItem: fuels.find((f) => f.id === fuel),
			formData,
		});

		const storedUser = JSON.parse(localStorage.getItem("user") || "null");
		const token = storedUser?.token || "";
		let decryptedCustId = null;
		if (token && storedUser?.id && secretKey) {
			try {
				const bytes = CryptoJS.AES.decrypt(storedUser.id, secretKey);
				decryptedCustId = bytes.toString(CryptoJS.enc.Utf8);
			} catch (err) {
				console.warn("Failed to decrypt customer id", err);
			}
		}

		// Logged-in: save via API first, then notify parent (never submit outer BookServiceModal form)
		if (token && decryptedCustId) {
			const payload = {
				custID: decryptedCustId,
				brandID: formData.brandID || brand,
				modelID: formData.modelID || model,
				fuelTypeID: formData.fuelTypeID || fuel,
				VehicleNumber: formData.registrationNumber,
				yearOfPurchase: formData.yearOfPurchase,
				engineType: formData.engineType,
				kilometersDriven: formData.kilometerDriven,
				transmissionType: formData.transmissionType,
				CreatedBy: decryptedCustId,
			};

			try {
				const resp = await axios.post(`${BaseURL}CustomerVehicles/InsertCustomerVehicle`, payload);
				const apiData = resp?.data ?? {};
				const newVehicleId = Number(
					apiData.vehicleID ?? apiData.VehicleID ?? apiData.vehicleId ?? apiData.data?.vehicleID ?? 0
				);

				if (apiData?.status === false || apiData?.vehicleID === -1 || newVehicleId < 0) {
					const errorMessage = apiData?.message || "Unable to save vehicle. Please check details.";
					if (showAlert) showAlert("Error", errorMessage, 4000, "error");
					return;
				}

				const saved = {
					...selectedCarDetails,
					id: newVehicleId || selectedCarDetails.id || 0,
					VehicleID: newVehicleId || selectedCarDetails.VehicleID || 0,
					vehicleID: newVehicleId || selectedCarDetails.vehicleID || 0,
					vehicleNumber: formData.registrationNumber,
					VehicleNumber: formData.registrationNumber,
					registrationNumber: formData.registrationNumber,
					brandID: Number(formData.brandID || brand) || 0,
					modelID: Number(formData.modelID || model) || 0,
					fuelTypeID: Number(formData.fuelTypeID || fuel) || 0,
					yearOfPurchase: Number(formData.yearOfPurchase) || 0,
					YearOfPurchase: Number(formData.yearOfPurchase) || 0,
					engineType: formData.engineType,
					kilometersDriven: Number(formData.kilometerDriven) || 0,
					KilometersDriven: Number(formData.kilometerDriven) || 0,
					kilometerDriven: formData.kilometerDriven,
					transmissionType: formData.transmissionType,
				};

				localStorage.setItem("selectedCarDetails", JSON.stringify(saved));
				if (storedUser?.identifier) {
					const userCarKey = `selectedCar_${storedUser.identifier}`;
					localStorage.setItem(userCarKey, JSON.stringify(saved));
				}
				try {
					window.dispatchEvent(new CustomEvent("selectedCarUpdated"));
				} catch (_) {}

				fetchSavedVehicles();
				if (onCarSaved) onCarSaved(saved);
				if (onClose) onClose();
			} catch (err) {
				console.error("Error inserting customer vehicle:", err);
				const msg =
					err?.response?.data?.message ||
					err?.message ||
					"Failed to save vehicle. Please try again.";
				if (showAlert) showAlert("Error", msg, 4000, "error");
			}
			return;
		}

		// Guest (no token): keep selection locally only
		if (!token) {
			localStorage.setItem("selectedCarDetails", JSON.stringify(selectedCarDetails));
			if (storedUser?.identifier) {
				const userCarKey = `selectedCar_${storedUser.identifier}`;
				localStorage.setItem(userCarKey, JSON.stringify(selectedCarDetails));
			}
			try {
				window.dispatchEvent(new CustomEvent("selectedCarUpdated"));
			} catch (_) {}
			if (onCarSaved) onCarSaved(selectedCarDetails);
			if (onClose) onClose();
			return;
		}

		if (showAlert) {
			showAlert("Error", "Could not read your account. Please sign in again.", 4000, "error");
		}
	};



	


	const handleBrandSelect = (id) => {
		setSelectionError("");
		setBrand(id);
		setModel("");
		setShowBrandPopup(false);
		fetchModels(id);
		setTimeout(() => {
			setShowModelPopup(true);
		}, 100);
	};

	const handleModelSelect = (id) => {
		setSelectionError("");
		setModel(id);
		setShowModelPopup(false);
		setTimeout(() => {
			setShowFuelPopup(true);
		}, 100);
	};

	const handleFuelSelect = (id) => {
		setSelectionError("");
		setFuel(id);
		setShowFuelPopup(false);
	};

	const triadIncomplete = !brand || !model || !fuel;
	const savePrimaryBlocked =
		!String(formData.registrationNumber || "").trim() || !!regError || triadIncomplete;
	const savePrimaryTitle = !String(formData.registrationNumber || "").trim()
		? "Enter registration number first"
		: regError
			? "Fix registration number errors"
			: triadIncomplete
				? "Select Brand, Model & Fuel — all three are required to save"
				: "";

	if (!isVisible) return null;

	/* Portal keeps this UI outside parent forms (e.g. BookServiceModal) so Save cannot submit the enquiry form */
	return createPortal(
		<div className="choose-car-modal visible">
			<div className="modal-content" ref={modalRef}>
				<button className="modal-close" onClick={onClose}>
					×
				</button>
				{/* Mobile number verification - SignIn-like form */}
				{!isLoggedIn && (
				<div className="mb-3 p-3 rounded  bg-white">
					<h6 className="mb-3">Please Verify Your Mobile Number</h6>
					<form
						onSubmit={(e) => {
							e.preventDefault();
							e.stopPropagation();
							return otpSent ? handleVerifyOTP(e) : handleSendOTP(e);
						}}
					>
						<div className="mb-3 text-start">
							<label className="form-label">Mobile Number</label>
							<div className="input-group">
								<input
									type="text"
									inputMode="numeric"
									pattern="[0-9]*"
									className="form-control"
									placeholder="Enter mobile number"
									value={identifier}
									onChange={(e) => {
										const value = e.target.value.replace(/\D/g, "");
										if (value.length <= 10) setIdentifier(value);
									}}
									maxLength={10}
									required
								/>
							</div>
						</div>

						{otpSent && (
							<>
								<label className="form-label text-start">Enter OTP</label>
								<div className="mb-3 text-start input-group">
									<input
										type="text"
										className="form-control"
										placeholder="Enter OTP"
										value={otp}
										onChange={(e) => setOtp(e.target.value)}
										required
									/>
									<div className="input-group-append">
										<button
											type="button"
											onClick={handleSendOTP}
											disabled={loginLoading}
											className="btn btn-outline-secondary"
											title="Resend OTP"
											style={{ padding: "12px 17px" }}
										>
											Resend
										</button>
									</div>
								</div>
								{otpSent && !otpExpired && <small className="text-muted">OTP will expire in {timer}s</small>}
								{otpExpired && <div className="text-danger small mb-2">OTP expired. Please resend.</div>}
							</>
						)}

						<div className="text-center mb-2">
							<button
								type="submit"
								className={`btn btn-primary btn-sm line-none ${loginLoading || otpExpired ? "disabled" : ""}`}
								disabled={loginLoading || otpExpired}
							>
								{loginLoading ? "Sending..." : otpSent ? (otpExpired ? "OTP Expired" : "Verify OTP") : "Send OTP"}
							</button>
						</div>
					</form>
				</div>
				)}

				{isLoggedIn && (
				<>
				<h6>Select Your Car Type</h6>
				{/* Dropdown */}
				<div className="mb-4 d-none">
					<select className="form-select" value={selectionMethod} onChange={(e) => setSelectionMethod(e.target.value)}>
						<option value="registration">Registration Number</option>
						<option value="manual">Manual Selection</option>
					</select>
				</div>

				{/* Saved vehicles chooser (only if exist) */}
				{isLoggedIn && savedVehicles.length > 0 && (
					<div className="mb-3 p-3 rounded border bg-white">
						<div className="row g-2 align-items-end">
							<div className="col-12 col-md-8">
								<label className="form-label small">Choose from saved cars</label>
								<select
									className="form-select"
									value={selectedSavedVehicleId}
									onChange={(e) => setSelectedSavedVehicleId(e.target.value)}
								>
									<option value="">Select a saved car</option>
									{savedVehicles.map((v) => (
										<option key={v.VehicleID || v.vehicleID} value={v.VehicleID || v.vehicleID}>
											{(v.BrandName || v.brandName || 'Brand')} {(v.ModelName || v.modelName || 'Model')} - {(v.VehicleNumber || v.VehicleRegNo || '').toUpperCase()}
										</option>
									))}
								</select>
							</div>
							<div className="col-12 col-md-4">
								<button type="button" className="btn btn-primary btn-confirm px-4 py-2" disabled={!selectedSavedVehicleId} onClick={handleChooseSavedVehicle}>
									Use This Car
								</button>
							</div>
						</div>
					</div>
				)}
				<h6>Or Add New Car</h6>
				<form
					onSubmit={(e) => {
						e.preventDefault();
						e.stopPropagation();
						handleSubmit(e);
					}}
				>
					{selectionMethod === "registration" ? (
						<div className="mb-4">
							<label className="form-label">Enter Vehicle Registration Number</label>
							<input
								type="text"
								className="form-control"
								value={registrationNumber}
								onChange={(e) => setRegistrationNumber(e.target.value)}
								placeholder="e.g. TG12AB1234"
							/>
						</div>
					) : (
						<>
                    {/* Vehicle Details FIRST */}
                    <div className="mt-3 text-start">
                        <div className="row g-2">
                            {/* Mobile number + OTP were moved to a separate block above */}
								<div className="col-12 col-md-4">
									<label className="form-label small">Registration Number <span className="text-danger">*</span></label>
									<div className="position-relative">
										<input
											type="text"
											className={`form-control ${regError ? 'is-invalid' : ''}`}
											value={formData.registrationNumber}
											onChange={handleVehicleNumberChange}
											style={{ textTransform: 'uppercase' }}
											placeholder="e.g., TG12AB1234"
											required
										/>
										{isLoadingVehicleData && (
											<div className="position-absolute top-50 end-0 translate-middle-y me-3">
												<div className="spinner-border spinner-border-sm text-primary" role="status">
													<span className="visually-hidden">Loading...</span>
												</div>
											</div>
										)}
										{regMessage && <div className="invalid-feedback">{regMessage}</div>}
									</div>
								</div>
								<div className="col-12 col-md-4">
									<label className="form-label small">Year of Purchase</label>
									<input
										type="text"
										className={`form-control ${yearError ? 'is-invalid' : ''}`}
										value={formData.yearOfPurchase}
										onChange={(e) => {
											const value = e.target.value.replace(/\D/g, '').slice(0, 4);
											setFormData((p) => ({ ...p, yearOfPurchase: value }));
											if (value.length === 4) {
												const currentYear = new Date().getFullYear();
												const year = parseInt(value, 10);
												if (isNaN(year) || year < 1900 || year > currentYear) {
													setYearError(true);
												} else {
													setYearError(false);
												}
											} else {
												setYearError(false);
											}
										}}
										placeholder="e.g., 2020"
									/>
									{yearError && <div className="invalid-feedback">Enter a valid year.</div>}
								</div>
								
								<div className="col-12 col-md-4">
									<label className="form-label small">Kilometers Driven</label>
									<input
										type="text"
										className="form-control"
										value={formData.kilometerDriven}
                                        onChange={(e) => setFormData((p) => ({ ...p, kilometerDriven: e.target.value.replace(/\D/g, '') }))}
										placeholder="e.g., 25000"
									/>
								</div>
								
							</div>
						</div>

						{/* THEN: Choose Brand, Model & Fuel (all required to save) */}
            <div className="mb-4 mt-4">
						<label className="form-label fw-semibold d-block text-center">
							Choose Brand, Model & Fuel{" "}
							<span className="text-danger" title="All three are required to save">
								*
							</span>
						</label>
						<p className="text-muted small mb-3 text-center px-1" style={{ maxWidth: 420, marginLeft: "auto", marginRight: "auto" }}>
							Select <strong>Brand</strong>, then <strong>Model</strong>, then <strong>Fuel</strong>. All three are required before you can save a new car.
						</p>
						<div className="d-flex gap-3 flex-wrap justify-content-center">
							{/* Brand Card */}
							<div
								onClick={() => {
									if (!loadingBrands) setShowBrandPopup(true);
								}}
								className={`rounded shadow-sm text-center p-3 car-box ${
									brand
										? "border-primary border-2"
										: selectionError && !brand
											? "border-danger border-2"
											: "border"
								} bg-white hover-shadow`}
								style={{
									width: 120,
									height: 120,
									cursor: loadingBrands ? "not-allowed" : "pointer",
									transition: "0.3s",
									display: "flex",
									flexDirection: "column",
									alignItems: "center",
									justifyContent: "center",
									opacity: loadingBrands ? 0.6 : 1,
								}}
							>
								<div className="fw-semibold small mb-2 text-dark">Brand</div>
								{loadingBrands ? (
									<div
										className="skeleton-loader"
										style={{
											width: 70,
											height: 70,
											backgroundColor: "#e0e0e0",
											borderRadius: "4px",
											animation: "pulse 1.5s ease-in-out infinite",
										}}
									></div>
								) : brand ? (
									<img
										src={brands.find((b) => b.id === brand)?.logo}
										alt="Brand Logo"
										style={{ width: 70, height: 70, objectFit: "contain" }}
									/>
								) : (
									<div className="text-muted small">Choose</div>
								)}
							</div>

							{/* Model Card */}
							<div
								onClick={() => {
									if (!loadingModels && brand) setShowModelPopup(true);
								}}
								className={`rounded shadow-sm text-center p-3 car-box ${
									model
										? "border-primary border-2"
										: selectionError && !model
											? "border-danger border-2"
											: "border"
								} bg-white hover-shadow`}
								style={{
									width: 120,
									height: 120,
									cursor: loadingModels || !brand ? "not-allowed" : "pointer",
									opacity: loadingModels || !brand ? 0.6 : 1,
									transition: "0.3s",
									display: "flex",
									flexDirection: "column",
									alignItems: "center",
									justifyContent: "center",
								}}
							>
								<div className="fw-semibold small mb-2 text-dark">Model</div>
								{loadingModels ? (
									<div
										className="skeleton-loader"
										style={{
											width: 70,
											height: 70,
											backgroundColor: "#e0e0e0",
											borderRadius: "4px",
											animation: "pulse 1.5s ease-in-out infinite",
										}}
									></div>
								) : model ? (
									<img
										src={models.find((m) => m.id === model)?.logo}
										alt="Model Image"
										style={{ width: 70, height: 70, objectFit: "contain" }}
									/>
								) : (
									<div className="text-muted small">Choose</div>
								)}
							</div>

							{/* Fuel Card */}
							<div
								onClick={() => {
									if (model) setShowFuelPopup(true);
								}}
								className={`rounded shadow-sm text-center p-3 car-box ${
									fuel
										? "border-primary border-2"
										: selectionError && !fuel
											? "border-danger border-2"
											: "border"
								} bg-white hover-shadow`}
								style={{
									width: 120,
									height: 120,
									cursor: !model ? "not-allowed" : "pointer",
									opacity: !model ? 0.6 : 1,
									transition: "0.3s",
									display: "flex",
									flexDirection: "column",
									alignItems: "center",
									justifyContent: "center",
								}}
							>
								<div className="fw-semibold small mb-2 text-dark">Fuel</div>
								{fuel ? (
									<div>
										{(() => {
											const selectedFuel = fuels.find((f) => f.id === fuel);
											console.log("Fuel card render - fuel ID:", fuel, "selectedFuel:", selectedFuel);
											return (
												<>
													<img
														src={selectedFuel?.image}
														alt="Fuel Type"
														style={{ width: 45, height: 45, objectFit: "contain" }}
														onError={(e) => {
															console.log("Fuel image error - fuel ID:", fuel, "selectedFuel:", selectedFuel, "image URL:", selectedFuel?.image);
															e.target.style.display = 'none';
														}}
													/>
													<div className="small mt-1" style={{ fontSize: '10px', color: '#666' }}>
														{selectedFuel?.name || 'Unknown'}
													</div>
												</>
											);
										})()}
									</div>
								) : (
									<div className="text-muted small">Choose</div>
								)}
							</div>
						</div>

						{/* Chosen result display */}
						{(brand || model || fuel) && (
							<div className="mt-3 px-2 py-1 bg-white rounded shadow-sm text-center small fw-bold text-secondary">
								{brand && brands.find((b) => b.id === brand)?.name}
								{brand && model && " - "}
								{model && models.find((m) => m.id === model)?.name}
								{(brand || model) && fuel && " - "}
								{fuel && fuels.find((f) => f.id === fuel)?.name}
							</div>
						)}
						{selectionError && (
							<div
								className="alert alert-danger py-2 px-3 small mt-3 mb-0 text-center"
								role="alert"
								style={{ maxWidth: 420, marginLeft: "auto", marginRight: "auto" }}
							>
								{selectionError}
							</div>
						)}
					</div>
            </>
					)}

					


					{/* Booking Vehicle Details fields were moved above; keeping this block removed */}

					{/* Buttons */}
					<div className="d-flex justify-content-center gap-2 mt-3">
						<button type="button" className="btn btn-warning py-2 px-4" onClick={onClose}>
							Cancel
						</button>
						<button
							type="button"
							className="btn btn-primary py-2 px-4"
							disabled={savePrimaryBlocked}
							title={savePrimaryTitle || undefined}
							onClick={(e) => {
								e.preventDefault();
								e.stopPropagation();
								handleSubmit(e);
							}}
						>
							Save
						</button>
					</div>
				</form>
				</>
				)}
			</div>

			{isLoggedIn && showBrandPopup && (
				<BrandPopup
					brands={brands}
					selected={brand}
					onSelect={handleBrandSelect}
					onClose={() => setShowBrandPopup(false)}
				/>
			)}

			{isLoggedIn && showModelPopup && (
				<ModelPopup
					models={models}
					selected={model}
					onSelect={handleModelSelect}
					onClose={() => setShowModelPopup(false)}
					loading={loadingModels}
				/>
			)}

			{isLoggedIn && showFuelPopup && (
				<FuelPopup
					fuels={fuels}
					selected={fuel}
					onSelect={handleFuelSelect}
					onClose={() => setShowFuelPopup(false)}
				/>
			)}
		</div>,
		document.body
	);
};

export default ChooseCarModal;
